<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';

import OutcomeBanner from '../../components/OutcomeBanner.vue';
import { api, type Outcome } from '../../api/client.js';
import { uuid } from '../../api/documents.js';
import { isSaoJoaoDemo, saoJoaoRoute } from './sao-joao-demo.js';

/**
 * Cenário salvo e preparação de cadastros.
 *
 * M02 adiciona a ordem de execução e o acompanhamento. O circuito continua
 * sendo executado pelo processo Java separado; a tela não conhece a chave do
 * dispositivo e nunca simula leitura dentro do navegador.
 *
 * O perímetro e a rota são informados por coordenadas ordenadas; imagem de
 * satélite e editor amigável não são critério deste incremento.
 */
const props = defineProps<{
  tenantId: string;
  tenantName?: string;
  farms: { id: string; name: string }[];
  pastures: { id: string; name: string }[];
  devices?: { id: string; label: string; origin: string; status: string }[];
}>();

interface PlanItem { ordinal: number; state: string; epcRaw: string; errorCode: string | null }
interface Plan { planId: string; state: string; items: PlanItem[] }
interface ScenarioSummary {
  scenarioId: string;
  farmId: string;
  name: string;
  revision: string;
  latestPlanId: string | null;
}
interface ScenarioDocument {
  scenarioId: string;
  farmId: string;
  name: string;
  seed: number;
  revision: string;
  routes: { points: { latitude: number; longitude: number }[] }[];
  pastureAnimalCounts: { pastureId: string; animalCount: number }[];
}
interface SimulationRun {
  runId: string;
  state: string;
  sessionId: string | null;
  progress: {
    recordsProduced: number;
    recordsAcknowledged: number;
    batchesSent: number;
    batchesAcknowledged: number;
    cleanupAuthorized: boolean;
  };
  errorCode: string | null;
  detail: string | null;
}

const scenarioId = ref(uuid());
const scenarios = ref<ScenarioSummary[]>([]);
const selectedScenarioId = ref('');
const persisted = ref(false);
const name = ref('CIRCUITO NORTE');
const seed = ref(20260921);
const farmId = ref(props.farms[0]?.id ?? '');
const animalsPerPasture = ref(60);
const routeText = ref(
  isSaoJoaoDemo(props.tenantName ?? '')
    ? saoJoaoRoute().map((point) => `${point.latitude},${point.longitude}`).join('\n')
    : '',
);

const outcome = ref<Outcome | null>(null);
const plan = ref<Plan | null>(null);
const running = ref(false);
const preparing = ref(false);
const generationRequestId = ref(uuid());
const savedRevision = ref<string | null>(null);
const selectedDeviceId = ref('');
const runId = ref<string | null>(null);
const simulation = ref<SimulationRun | null>(null);
const simulationBusy = ref(false);

const simulatedDevices = computed(() =>
  (props.devices ?? []).filter(
    (device) => device.origin === 'simulated' && device.status === 'active',
  ),
);

const progress = computed(() => {
  if (plan.value === null) return { applied: 0, failed: 0, total: 0 };
  const applied = plan.value.items.filter((item) => item.state === 'applied').length;
  const failed = plan.value.items.filter((item) => item.state === 'failed').length;
  return { applied, failed, total: plan.value.items.length };
});

const failures = computed(() => (plan.value?.items ?? []).filter((item) => item.state === 'failed'));

function resetScenario(): void {
  scenarioId.value = uuid();
  selectedScenarioId.value = '';
  persisted.value = false;
  name.value = 'CIRCUITO NORTE';
  seed.value = 20260921;
  farmId.value = props.farms[0]?.id ?? '';
  animalsPerPasture.value = 60;
  routeText.value = isSaoJoaoDemo(props.tenantName ?? '')
    ? saoJoaoRoute().map((point) => `${point.latitude},${point.longitude}`).join('\n')
    : '';
  generationRequestId.value = uuid();
  savedRevision.value = null;
  plan.value = null;
  runId.value = null;
  simulation.value = null;
}

async function loadScenario(id: string): Promise<void> {
  const result = await api.readScenario<ScenarioDocument>(id, props.tenantId);
  if (result.status !== 200) return;

  scenarioId.value = result.body.scenarioId;
  selectedScenarioId.value = result.body.scenarioId;
  persisted.value = true;
  name.value = result.body.name;
  seed.value = result.body.seed;
  farmId.value = result.body.farmId;
  routeText.value = (result.body.routes?.[0]?.points ?? [])
    .map((point) => `${point.latitude},${point.longitude}`)
    .join('\n');
  const counts = result.body.pastureAnimalCounts ?? [];
  if (counts[0] !== undefined) animalsPerPasture.value = counts[0].animalCount;
  savedRevision.value = result.body.revision;
  plan.value = null;

  const latestPlanId = scenarios.value.find((item) => item.scenarioId === id)?.latestPlanId;
  if (latestPlanId !== null && latestPlanId !== undefined) {
    const loadedPlan = await api.readPlan<Plan>(latestPlanId, props.tenantId);
    if (loadedPlan.status === 200) plan.value = loadedPlan.body;
  }
}

async function loadScenarios(preferredId?: string): Promise<void> {
  const result = await api.listScenarios<{ items: ScenarioSummary[] }>(props.tenantId);
  if (result.status !== 200) return;
  scenarios.value = result.body.items;
  const id = preferredId !== undefined && result.body.items.some((item) => item.scenarioId === preferredId)
    ? preferredId
    : result.body.items[0]?.scenarioId;
  if (id !== undefined) await loadScenario(id);
}

async function selectScenario(): Promise<void> {
  if (selectedScenarioId.value === '') {
    resetScenario();
    return;
  }
  await loadScenario(selectedScenarioId.value);
}

function parseRoute(): { latitude: number; longitude: number }[] {
  return routeText.value
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const [latitude, longitude] = line.split(',').map((part) => Number.parseFloat(part.trim()));
      return { latitude: latitude!, longitude: longitude! };
    });
}

async function save(): Promise<void> {
  const points = parseRoute();
  const document = {
    schemaVersion: 'rfid.mvp.scenario-configuration/1.0.0',
    scenarioId: scenarioId.value,
    tenantId: props.tenantId,
    farmId: farmId.value,
    requestId: uuid(),
    name: name.value,
    seed: seed.value,
    routes:
      points.length >= 2
        ? [{
            routeId: uuid(),
            name: 'ROTA',
            speedMetersPerSecond: isSaoJoaoDemo(props.tenantName ?? '') ? 5 : 1.4,
            points,
          }]
        : [],
    pastureAnimalCounts: props.pastures.map((pasture) => ({
      pastureId: pasture.id,
      animalCount: animalsPerPasture.value,
    })),
  };
  const result = persisted.value
    ? await api.updateScenario<Record<string, unknown>>(scenarioId.value, document)
    : await api.saveScenario<Record<string, unknown>>(document);

  outcome.value =
    result.status === 201 || result.status === 200
      ? { kind: 'applied', message: 'Cenário salvo. Nada foi executado.', errorCode: null }
      : {
          kind: 'rejected',
          message: String((result.body as { detail?: string }).detail ?? 'recusado'),
          errorCode: (result.body as { errorCode?: string }).errorCode ?? null,
        };
  if (result.status === 201 || result.status === 200) {
    savedRevision.value = String(result.body.revision ?? '1');
    persisted.value = true;
    selectedScenarioId.value = scenarioId.value;
    await loadScenarios(scenarioId.value);
  }
}

async function prepare(): Promise<void> {
  if (preparing.value) return;
  preparing.value = true;
  try {
    const result = await api.createPlan<Plan & { errorCode?: string; detail?: string }>(scenarioId.value, {
      tenantId: props.tenantId,
      requestId: generationRequestId.value,
      pastureAnimalCounts: props.pastures.map((pasture) => ({
        pastureId: pasture.id,
        animalCount: animalsPerPasture.value,
      })),
    });
    if (result.status >= 300 || typeof result.body.planId !== 'string') {
      outcome.value = {
        kind: 'rejected',
        message: result.body.detail ?? 'Não foi possível preparar o plano.',
        errorCode: result.body.errorCode ?? null,
      };
      return;
    }
    plan.value = result.body;
    outcome.value = {
      kind: 'applied',
      message: `Plano persistido com ${result.body.items?.length ?? 0} itens. Nenhum cadastro enviado ainda.`,
      errorCode: null,
    };
  } catch (error) {
    outcome.value = {
      kind: 'rejected',
      message: error instanceof Error ? error.message : 'Falha de comunicação ao preparar o plano.',
      errorCode: 'PLAN_PREPARATION_FAILED',
    };
  } finally {
    preparing.value = false;
  }
}

/**
 * Executa em lotes e recarrega o progresso do SERVIDOR entre eles.
 *
 * Fechar a aba no meio não perde nada: o plano e o estado de cada item estão
 * persistidos, e retomar continua de onde parou sem duplicar.
 */
async function run(chunk = 200): Promise<void> {
  const current = plan.value;
  if (current === null) return;
  const planId = current.planId;

  running.value = true;
  try {
    let remaining = 1;
    while (remaining > 0) {
      const result = await api.runPlan<{ remaining?: number }>(planId, {
        tenantId: props.tenantId,
        maxItems: chunk,
      });
      const refreshed = await api.readPlan<Plan>(planId, props.tenantId);
      plan.value = refreshed.body;
      if (result.body.remaining === undefined) break;
      remaining = result.body.remaining;
    }
  } finally {
    running.value = false;
  }
}

async function refresh(): Promise<void> {
  const current = plan.value;
  if (current === null) return;
  const refreshed = await api.readPlan<Plan>(current.planId, props.tenantId);
  plan.value = refreshed.body;
}

async function executeCircuit(): Promise<void> {
  if (savedRevision.value === null || selectedDeviceId.value === '') return;
  simulationBusy.value = true;
  try {
    const result = await api.simulationControl<{
      runId: string | null;
      state: string;
      errorCode: string | null;
      detail: string | null;
    }>({
      schemaVersion: 'rfid.mvp.simulation-control/1.0.0',
      requestId: uuid(),
      tenantId: props.tenantId,
      scenarioId: scenarioId.value,
      deviceInstallationId: selectedDeviceId.value,
      action: 'execute',
      referenceRevision: savedRevision.value,
      config: {
        seed: seed.value,
        circuit: 'mock-reader',
        // Exigido pelo contrato, mas sem autoridade quando há rota: a API
        // congela a duração derivada do comprimento da rota e da velocidade.
        durationMs: 30_000,
        exclusive: true,
      },
    });
    if (result.body.runId !== null && result.status < 300) {
      runId.value = result.body.runId;
      outcome.value = {
        kind: 'applied',
        message: 'Execução criada. Estado planned significa aguardando o executor Java.',
        errorCode: null,
      };
      await refreshSimulation();
    } else {
      outcome.value = {
        kind: result.status === 409 ? 'conflict' : 'rejected',
        message: result.body.detail ?? 'Execução recusada.',
        errorCode: result.body.errorCode,
      };
    }
  } finally {
    simulationBusy.value = false;
  }
}

async function refreshSimulation(): Promise<void> {
  if (runId.value === null) return;
  const result = await api.simulationRun<SimulationRun>(runId.value);
  simulation.value = result.body;
}

async function stopCircuit(): Promise<void> {
  if (selectedDeviceId.value === '') return;
  simulationBusy.value = true;
  try {
    const result = await api.simulationControl<{
      runId: string | null;
      state: string;
      errorCode: string | null;
      detail: string | null;
    }>({
      schemaVersion: 'rfid.mvp.simulation-control/1.0.0',
      requestId: uuid(),
      tenantId: props.tenantId,
      scenarioId: scenarioId.value,
      deviceInstallationId: selectedDeviceId.value,
      action: 'stop',
      referenceRevision: savedRevision.value ?? '1',
      config: {
        seed: seed.value,
        circuit: 'mock-reader',
        durationMs: 30_000,
        exclusive: true,
      },
    });
    if (result.body.runId !== null) runId.value = result.body.runId;
    await refreshSimulation();
  } finally {
    simulationBusy.value = false;
  }
}

onMounted(loadScenarios);
watch(() => props.tenantId, () => {
  resetScenario();
  void loadScenarios();
});
watch(() => props.farms, (farms) => {
  if (farmId.value === '' && farms[0] !== undefined) farmId.value = farms[0].id;
});

defineExpose({
  save, prepare, run, refresh, executeCircuit, refreshSimulation, stopCircuit,
  loadScenarios, selectScenario, resetScenario, plan,
});
</script>

<template>
  <section class="scenario">
    <h3>Cenário e preparação de cadastros</h3>
    <p>
      Salvar um cenário não executa nada. Depois de preparar os cadastros, uma rodada
      pode ser enviada ao executor Java. As posições estimadas são calculadas depois, pelo
      worker de processamento, e aparecem na área do cliente.
    </p>

    <div class="row">
      <label>
        Cenário
        <select
          v-model="selectedScenarioId"
          name="saved-scenario"
          @change="selectScenario"
        >
          <option value="">novo cenário</option>
          <option
            v-for="scenario in scenarios"
            :key="scenario.scenarioId"
            :value="scenario.scenarioId"
          >{{ scenario.name }}</option>
        </select>
      </label>
      <button
        type="button"
        @click="resetScenario"
      >
        Novo cenário
      </button>
    </div>

    <form
      class="grid"
      @submit.prevent="save"
    >
      <label>Nome <input
        v-model="name"
        name="scenario-name"
        required
      ></label>
      <label>Semente <input
        v-model.number="seed"
        name="scenario-seed"
        type="number"
        min="0"
      ></label>
      <label>
        Fazenda
        <select
          v-model="farmId"
          name="scenario-farm"
        >
          <option
            v-for="farm in farms"
            :key="farm.id"
            :value="farm.id"
          >{{ farm.name }}</option>
        </select>
      </label>
      <label>
        Animais por pasto
        <input
          v-model.number="animalsPerPasture"
          name="scenario-count"
          type="number"
          min="0"
          max="1000"
        >
      </label>
      <label class="wide">
        Rota (latitude,longitude por linha)
        <textarea
          v-model="routeText"
          name="scenario-route"
          rows="4"
        />
      </label>
      <button type="submit">
        Salvar cenário
      </button>
    </form>

    <OutcomeBanner :outcome="outcome" />

    <div class="row">
      <button
        type="button"
        :disabled="preparing"
        @click="prepare"
      >
        {{ preparing ? 'Preparando…' : 'Preparar plano de cadastros' }}
      </button>
      <button
        type="button"
        :disabled="plan === null || running"
        @click="run()"
      >
        Executar plano
      </button>
      <button
        type="button"
        :disabled="plan === null"
        @click="refresh"
      >
        Atualizar progresso
      </button>
    </div>

    <section
      v-if="plan"
      data-testid="plan-progress"
    >
      <h4>Progresso</h4>
      <p>
        {{ progress.applied }} de {{ progress.total }} aplicados;
        <strong>{{ progress.failed }}</strong> com falha. Estado: {{ plan.state }}.
      </p>
      <ul
        v-if="failures.length > 0"
        data-testid="plan-failures"
      >
        <li
          v-for="item in failures"
          :key="item.ordinal"
        >
          item {{ item.ordinal }} (<code>{{ item.epcRaw }}</code>): {{ item.errorCode }}
        </li>
      </ul>
    </section>

    <section class="simulation-controls">
      <h4>Execução de leituras simuladas</h4>
      <label>
        Dispositivo virtual
        <select
          v-model="selectedDeviceId"
          name="simulation-device"
        >
          <option value="">selecione</option>
          <option
            v-for="device in simulatedDevices"
            :key="device.id"
            :value="device.id"
          >
            {{ device.label }}
          </option>
        </select>
      </label>
      <div class="row">
        <button
          type="button"
          :disabled="savedRevision === null || selectedDeviceId === '' || simulationBusy"
          @click="executeCircuit"
        >
          Executar circuito
        </button>
        <button
          type="button"
          :disabled="runId === null || simulationBusy"
          @click="refreshSimulation"
        >
          Atualizar execução
        </button>
        <button
          type="button"
          :disabled="runId === null || simulationBusy"
          @click="stopCircuit"
        >
          Parar execução
        </button>
      </div>
      <p v-if="simulatedDevices.length === 0">
        Cadastre um dispositivo simulado e emita sua chave para o serviço executor.
      </p>
      <dl
        v-if="simulation"
        data-testid="simulation-progress"
      >
        <dt>Run</dt><dd><code>{{ simulation.runId }}</code></dd>
        <dt>Estado</dt><dd>{{ simulation.state }}</dd>
        <dt>Sessão</dt><dd><code>{{ simulation.sessionId ?? 'ainda não aberta' }}</code></dd>
        <dt>Registros</dt>
        <dd>{{ simulation.progress.recordsAcknowledged }} / {{ simulation.progress.recordsProduced }}</dd>
        <dt>Lotes</dt>
        <dd>{{ simulation.progress.batchesAcknowledged }} / {{ simulation.progress.batchesSent }}</dd>
        <dt>Limpeza autorizada</dt><dd>{{ simulation.progress.cleanupAuthorized ? 'sim' : 'não' }}</dd>
        <dt v-if="simulation.errorCode">
          Erro
        </dt>
        <dd v-if="simulation.errorCode">
          {{ simulation.errorCode }} — {{ simulation.detail }}
        </dd>
      </dl>
    </section>
  </section>
</template>

<style scoped>
.grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .75rem; align-items: end; }
.wide { grid-column: 1 / -1; }
.row { display: flex; gap: .75rem; margin: .75rem 0; }
</style>
